// Starts the sync engine for the signed-in company and provides it to every company screen (WEB_PLAN.md §3.1,
// §3.4, §4.6). One engine and one file-URL cache per session: a new account, company or role gets a fresh replica
// (auth/ has already wiped the old one by then). Mounted under RequireCompany, so `useSession()` is always set.
//
// - Sign-out pushes pending edits first: the engine is registered as auth/'s pending flusher while mounted.
// - not_member / suspended: the engine stops and deletes its replica; this asks auth/ to re-read memberships, which
//   moves the person to "removed" / "Your access was paused".
// - Live updates (doorbell.ts): the engine listens on the company's private Realtime channel unless VITE_DOORBELL=off,
//   and a `team` ring broadcasts gt:refresh so screens built on server queries refetch.
// - React StrictMode runs the effect twice in development: the engine is restartable, so it simply stops and starts.

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useAuth, useSession } from '@/auth/AuthProvider';
import { setPendingFlusher } from '@/auth/flush';
import { env } from '@/env';
import { requestRefresh } from '@/features/shell/refresh';
import { finishPendingCompanySetup } from '@/features/welcome/setupRunner';
import { supabase } from '@/supabase';
import { createSupabaseSyncApi } from './api';
import { SyncContext, type SyncContextValue } from './context';
import { listenForChanges } from './doorbell';
import { SyncEngine, type DoorbellFactory } from './engine';
import { FileUrls, createSupabaseStorageApi } from './files';
import { onLocalDataWiped } from './local';

const doorbell: DoorbellFactory | null = env.doorbell
  ? (companyId, onRing, onState) => listenForChanges(supabase, companyId, onRing, onState)
  : null;

export function SyncProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const { refreshMemberships } = useAuth();
  const refreshRef = useRef(refreshMemberships);
  useEffect(() => {
    refreshRef.current = refreshMemberships;
  }, [refreshMemberships]);
  /** Bumped after a wipe while still mounted: start over with an empty replica. */
  const [generation, setGeneration] = useState(0);

  // Constructing has no side effects; starting happens in the effect below.
  const value = useMemo<SyncContextValue>(
    () => ({
      engine: new SyncEngine({ api: createSupabaseSyncApi(supabase), doorbell }),
      files: new FileUrls(createSupabaseStorageApi(supabase), { mode: env.filesMode }),
    }),
    // A new engine per session (scope) and per wipe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session, generation],
  );

  useEffect(() => {
    const { engine, files } = value;
    let mounted = true;
    void engine.start(session);
    // A company created on /welcome whose seed didn't all reach the server (tab closed or reloaded, lost answer): send
    // the rest now, then pull so this tab sees it. iPhones need the settings item, or they open company setup.
    void finishPendingCompanySetup(session).then((result) => {
      if (mounted && result === 'done') void engine.pullNow();
    });
    setPendingFlusher((timeoutMs) => engine.flush(timeoutMs));
    const offRemoved = engine.onRemoved(() => {
      void refreshRef.current();
    });
    const offTeam = engine.onTeamChanged(requestRefresh);
    const offWiped = onLocalDataWiped(() => {
      // Local data was wiped while this company is still open (auth/ normally unmounts us first). Start over on the
      // next tick unless this provider is going away meanwhile.
      setTimeout(() => {
        if (mounted) setGeneration((g) => g + 1);
      }, 0);
    });
    return () => {
      mounted = false;
      offWiped();
      offTeam();
      offRemoved();
      setPendingFlusher(null);
      void engine.stop();
      files.clear();
    };
  }, [value, session]);

  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
}
